// src/pages/stock/EsimInventories.tsx
//
// Inventory list page
// - Uses shared table components (toolbar / frame / pagination).
// - Keeps URL query params as the single source of truth for filters + pagination.
// - Converts date filters between UI (YYYY-MM-DD) and API (timestamp string).
// - Navigates to detail page via the index (#) column.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  LinearProgress,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import {
  EsimInventoryParameterType,
  exportEsimInventoriesExcel,
  type EsimInventoryResponse,
  type GetEsimInventoriesParams,
} from '@apis/esim';

import { EsimStatus, EsimType } from '@models/stock';
import { ESIM_TYPE_OPTION_LIST } from '@constants/stocks';
import { getEsimStatusLabel } from '@constants/stockLabels';

import { useInventoryList } from '@queries/inventories/useInventoryList';
import { useInventoryCount } from '@queries/inventories/useInventoryCount';
import { useAllStockList } from '@queries/stocks/useAllStockList';

import { downloadBlob } from '@utils/file';
import { filterSearchParams } from '@utils/search';
import { dateToTimestamp, timestampToDate } from '@utils/time';
import { useListReturnNavigation } from '@hooks/useListReturnNavigation';

import EsimInventoriesSearchPanel from '@components/stock/EsimInventoriesSearchPanel';
import { EsimStatusDisplay, EsimTypeDisplay } from '@components/stock/EsimInventoryDisplays';
import type { SetSearchValueFn } from '@components/common/search';
import { usePageHeader } from '@components/common/pageHeader.context';

import {
  StandardPagination,
  StandardTableFrame,
  StandardTableToolbar,
  cellSx,
} from '@components/common/table';

const PAGE_SIZE = 50;
const PAGE_AUTO_SEARCH = { enabled: false, delayMs: 500 } as const;

type DraftSearchForm = GetEsimInventoriesParams;
type Option<T> = { label: string; value: T };

const clamp = (n: number, min: number, max: number) =>
  Math.min(max, Math.max(min, n));

const parseNumberParam = (value: string | null): number | undefined => {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const normalizeText = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const dashIfEmpty = (v: unknown): string => {
  if (v === null || v === undefined) return '-';
  if (typeof v === 'string') return v.trim() ? v : '-';
  return String(v);
};

const isValidEnumValue = <T extends string>(
  values: readonly string[],
  v: string | null,
): v is T => {
  if (!v) return false;
  return values.includes(v);
};

const safeTimestampToYmd = (ts: string | null | undefined): string | undefined => {
  if (!ts) return undefined;
  try {
    const ymd = timestampToDate(ts, 'YYYY-MM-DD');
    return ymd ? String(ymd) : undefined;
  } catch {
    return undefined;
  }
};

const ymdToTimestampParam = (
  ymd?: string | null,
  isEnd?: boolean,
): string | undefined => {
  const v = normalizeText(ymd);
  if (!v) return undefined;

  try {
    // For end date, include end-of-day; for start date, include start-of-day.
    const ts = isEnd ? dateToTimestamp(v, true, true) : dateToTimestamp(v, false, true, true);
    return String(ts);
  } catch {
    return undefined;
  }
};

/**
 * Convert draft (UI state) into applied (API) params.
 * - Trims text inputs
 * - Converts expireAtFrom/To from YYYY-MM-DD -> timestamp string
 * - Keeps parameterValue as string (API contract)
 */
const toAppliedParams = (draft: DraftSearchForm): GetEsimInventoriesParams => {
  const parameterValue = normalizeText(draft.parameterValue);

  return {
    stockId: typeof draft.stockId === 'number' ? draft.stockId : undefined,
    esimType: draft.esimType || undefined,
    esimStatus: draft.esimStatus || undefined,
    expireAtFrom: ymdToTimestampParam(draft.expireAtFrom) || undefined,
    expireAtTo: ymdToTimestampParam(draft.expireAtTo, true) || undefined,
    parameterType: draft.parameterType || EsimInventoryParameterType.LPA_URL,
    parameterValue,
  };
};

const readDraftFromUrl = (sp: URLSearchParams): DraftSearchForm => {
  const esimTypeRaw = sp.get('esimType');
  const esimStatusRaw = sp.get('esimStatus');
  const parameterTypeRaw = sp.get('parameterType');

  const esimType = isValidEnumValue<EsimType>(
    Object.values(EsimType) as unknown as string[],
    esimTypeRaw,
  )
    ? (esimTypeRaw as EsimType)
    : undefined;

  const esimStatus = isValidEnumValue<EsimStatus>(
    Object.values(EsimStatus) as unknown as string[],
    esimStatusRaw,
  )
    ? (esimStatusRaw as EsimStatus)
    : undefined;

  const parameterType = isValidEnumValue<EsimInventoryParameterType>(
    Object.values(EsimInventoryParameterType) as unknown as string[],
    parameterTypeRaw,
  )
    ? (parameterTypeRaw as EsimInventoryParameterType)
    : EsimInventoryParameterType.LPA_URL;

  return {
    stockId: parseNumberParam(sp.get('stockId')),
    esimType,
    esimStatus,
    expireAtFrom: safeTimestampToYmd(sp.get('expireAtFrom')),
    expireAtTo: safeTimestampToYmd(sp.get('expireAtTo')),
    parameterType,
    parameterValue: sp.get('parameterValue') || '',
  };
};

const buildUrlParams = (draft: DraftSearchForm, page: number) => {
  const applied = toAppliedParams(draft);

  const params: Record<string, string> = {};
  if (typeof applied.stockId === 'number') params.stockId = String(applied.stockId);
  if (applied.esimType) params.esimType = String(applied.esimType);
  if (applied.esimStatus) params.esimStatus = String(applied.esimStatus);

  if (applied.expireAtFrom) params.expireAtFrom = String(applied.expireAtFrom);
  if (applied.expireAtTo) params.expireAtTo = String(applied.expireAtTo);

  if (applied.parameterType) params.parameterType = String(applied.parameterType);

  const keyword = normalizeText(applied.parameterValue);
  if (keyword) params.parameterValue = keyword;

  return {
    ...filterSearchParams(params),
    page: String(page),
  };
};

const CODE_TEXT_SX = {
  display: 'block',
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: 12,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

const CodeText = ({
  value,
  maxWidth,
  truncate = true,
  color,
}: {
  value: unknown;
  maxWidth?: number | string;
  truncate?: boolean;
  color?: string;
}) => {
  const text = dashIfEmpty(value);
  const isEmpty = text === '-';

  return (
    <Typography
      component="span"
      variant="body2"
      title={isEmpty ? undefined : text}
      sx={{
        ...CODE_TEXT_SX,
        maxWidth,
        overflow: truncate ? 'hidden' : 'visible',
        textOverflow: truncate ? 'ellipsis' : 'clip',
        color: isEmpty ? 'text.disabled' : color ?? 'text.primary',
        fontWeight: isEmpty ? 600 : 500,
      }}
    >
      {text}
    </Typography>
  );
};

const getSerialOrRedeem = (row: EsimInventoryResponse) => {
  const serial = normalizeText(row.serialNo);
  if (serial) return serial;
  return normalizeText(row.redemptionCode) || '-';
};

const EsimInventories = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigateFromList } = useListReturnNavigation();
  const { setPageHeaderSlots } = usePageHeader();

  useEffect(() => {
    // This page does not provide extra header actions.
    setPageHeaderSlots({});
  }, [setPageHeaderSlots]);

  const [draftSearch, setDraftSearch] = useState<DraftSearchForm>(() =>
    readDraftFromUrl(searchParams),
  );

  const [appliedSearch, setAppliedSearch] = useState<GetEsimInventoriesParams>(() =>
    toAppliedParams(readDraftFromUrl(searchParams)),
  );

  const [page, setPage] = useState<number>(() => {
    const raw = searchParams.get('page');
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 1;
  });

  /**
   * Keep local UI state in sync with URL params.
   * URL params are treated as the single source of truth.
   */
  const searchParamsKey = useMemo(() => searchParams.toString(), [searchParams]);

  useEffect(() => {
    const nextDraft = readDraftFromUrl(searchParams);

    const raw = searchParams.get('page');
    const n = Number(raw);
    const nextPage = Number.isFinite(n) && n > 0 ? n : 1;

    setDraftSearch(nextDraft);
    setAppliedSearch(toAppliedParams(nextDraft));
    setPage(nextPage);
  }, [searchParams, searchParamsKey]);

  const { data, isLoading } = useInventoryList({
    page,
    pageSize: PAGE_SIZE,
    searchParam: appliedSearch,
  });

  // Count query must be based on the same appliedSearch to keep pagination consistent.
  const { data: totalCount, isLoading: isLoadingCount } = useInventoryCount(appliedSearch);

  const safeTotalCount = totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(safeTotalCount / PAGE_SIZE));

  const rows = (data?.data ?? []) as EsimInventoryResponse[];
  const isTableLoading = Boolean(isLoading || isLoadingCount);

  /* ------------------------------------------------------------------ */
  /* Options                                                            */
  /* ------------------------------------------------------------------ */

  const { data: stocksData } = useAllStockList();

  const stockOptions = useMemo<Array<Option<number>>>(() => {
    const list = stocksData?.data ?? [];
    return list.map((s) => ({ label: s.stockCode, value: s.stockId }));
  }, [stocksData]);

  const esimTypeOptions = useMemo<Array<Option<EsimType>>>(() => {
    return (ESIM_TYPE_OPTION_LIST as unknown as Array<Option<EsimType>>) ?? [];
  }, []);

  const esimStatusOptions = useMemo<Array<Option<EsimStatus>>>(() => {
    const values = Object.values(EsimStatus) as EsimStatus[];
    return values
      .map((v) => ({ value: v, label: getEsimStatusLabel(v) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  const parameterTypeOptions = useMemo<Array<Option<EsimInventoryParameterType>>>(() => {
    const values = Object.values(EsimInventoryParameterType) as EsimInventoryParameterType[];
    return values
      .map((v) => ({ value: v, label: v.split('_').join(' ') }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);

  /* ------------------------------------------------------------------ */
  /* Selected options (Autocomplete)                                     */
  /* ------------------------------------------------------------------ */

  const selectedStock = useMemo(() => {
    const id = draftSearch.stockId;
    if (typeof id !== 'number') return null;
    return stockOptions.find((o) => o.value === id) ?? null;
  }, [draftSearch.stockId, stockOptions]);

  const selectedEsimType = useMemo(() => {
    const v = draftSearch.esimType as EsimType | undefined;
    if (!v) return null;
    return esimTypeOptions.find((o) => o.value === v) ?? null;
  }, [draftSearch.esimType, esimTypeOptions]);

  const selectedEsimStatus = useMemo(() => {
    const v = draftSearch.esimStatus as EsimStatus | undefined;
    if (!v) return null;
    return esimStatusOptions.find((o) => o.value === v) ?? null;
  }, [draftSearch.esimStatus, esimStatusOptions]);

  const selectedParameterType = useMemo(() => {
    const v =
      (draftSearch.parameterType as EsimInventoryParameterType | undefined) ??
      EsimInventoryParameterType.LPA_URL;
    return parameterTypeOptions.find((o) => o.value === v) ?? null;
  }, [draftSearch.parameterType, parameterTypeOptions]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  /* ------------------------------------------------------------------ */
  /* setValue for shared Search components                               */
  /* ------------------------------------------------------------------ */

  const setValue: SetSearchValueFn<DraftSearchForm> = useCallback((key, value) => {
    setDraftSearch((prev) => {
      const next: DraftSearchForm = { ...prev };

      switch (key) {
        case 'parameterValue':
          next.parameterValue = typeof value === 'string' ? value : '';
          return next;

        case 'expireAtFrom':
        case 'expireAtTo':
          (next as any)[key] = value ? String(value) : undefined;
          return next;

        default:
          (next as any)[key] = value ?? undefined;
          return next;
      }
    });
  }, []);

  /* ------------------------------------------------------------------ */
  /* Apply / Reset / Pagination                                          */
  /* ------------------------------------------------------------------ */

  const applyFilters = useCallback(
    (nextDraft?: DraftSearchForm) => {
      const next = nextDraft ?? draftSearch;
      setAppliedSearch(toAppliedParams(next));
      setPage(1);
      setSearchParams(buildUrlParams(next, 1));
    },
    [draftSearch, setSearchParams],
  );

  const handleSearch = useCallback(() => applyFilters(), [applyFilters]);

  const handleReset = useCallback(() => {
    const cleared: DraftSearchForm = {
      stockId: undefined,
      esimType: undefined,
      esimStatus: undefined,
      expireAtFrom: undefined,
      expireAtTo: undefined,
      parameterType: EsimInventoryParameterType.LPA_URL,
      parameterValue: '',
    };

    setDraftSearch(cleared);
    applyFilters(cleared);
  }, [applyFilters]);

  const handleChangePage = useCallback(
    (next: number) => {
      const safeNext = clamp(next, 1, totalPages);
      setPage(safeNext);
      setSearchParams(buildUrlParams(draftSearch, safeNext));
    },
    [draftSearch, setSearchParams, totalPages],
  );

  /* ------------------------------------------------------------------ */
  /* Export                                                             */
  /* ------------------------------------------------------------------ */

  const [isExporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    if (isExporting) return;

    try {
      setExporting(true);
      const res = await exportEsimInventoriesExcel(appliedSearch);
      const fileName = `esim-inventories-${new Date().toISOString()}.xlsx`;
      downloadBlob(res, fileName);
    } finally {
      setExporting(false);
    }
  }, [appliedSearch, isExporting]);

  /* ------------------------------------------------------------------ */
  /* Navigation                                                         */
  /* ------------------------------------------------------------------ */

  const handleOpenDetail = useCallback(
    (esimInventoryId: number) => {
      navigateFromList(`/stocks/esim-inventory/${esimInventoryId}`);
    },
    [navigateFromList],
  );

  const columnsCount = 9;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box mt={2}>
        <EsimInventoriesSearchPanel
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          draftSearch={draftSearch}
          setValue={setValue}
          stockOptions={stockOptions}
          esimTypeOptions={esimTypeOptions}
          esimStatusOptions={esimStatusOptions}
          parameterTypeOptions={parameterTypeOptions}
          selectedStock={selectedStock}
          selectedEsimType={selectedEsimType}
          selectedEsimStatus={selectedEsimStatus}
          selectedParameterType={selectedParameterType}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={handleExport}
          exportLoading={isExporting}
          autoSearch={PAGE_AUTO_SEARCH}
        />
      </Box>

      <StandardTableToolbar
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={safeTotalCount}
        onChangePage={handleChangePage}
        showTotalCount
        showPageJump
      />

      {(isTableLoading || isExporting) && (
        <Box mt={1}>
          <LinearProgress />
        </Box>
      )}

      <Box mt={1}>
        <StandardTableFrame<EsimInventoryResponse>
          containerSx={{ borderRadius: 2 }}
          minWidth={1550}
          columnsCount={columnsCount}
          isLoading={isTableLoading}
          loadingVariant="skeleton"
          skeletonRows={8}
          emptyText="No inventories found."
          head={
            <TableRow>
              <TableCell width={80} align="right">
                #
              </TableCell>
              <TableCell sx={{ minWidth: 140 }}>Stock</TableCell>
              <TableCell width={110}>Type</TableCell>
              <TableCell width={115}>Status</TableCell>
              <TableCell sx={{ minWidth: 190 }}>ICCID</TableCell>
              <TableCell sx={{ minWidth: 150 }}>MSISDN</TableCell>
              <TableCell sx={{ minWidth: 160 }}>Serial / Redeem</TableCell>
              <TableCell sx={{ minWidth: 520 }}>LPA URL</TableCell>
              <TableCell width={130} align="center">
                Expiry
              </TableCell>
            </TableRow>
          }
          rows={rows}
          getRowKey={(row) => String(row.esimInventoryId)}
          renderRow={(row, index) => {
            const id = Number(row.esimInventoryId);

            return (
              <>
                <TableCell align="right" sx={cellSx.number}>
                  {(page - 1) * PAGE_SIZE + index + 1}
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleOpenDetail(id)}
                    sx={{
                      minWidth: 0,
                      p: 0,
                      color: 'text.primary',
                      fontWeight: 700,
                      lineHeight: 1.4,
                      textTransform: 'none',
                      textDecoration: 'underline',
                      textUnderlineOffset: '3px',
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {dashIfEmpty(row.stock?.stockCode)}
                  </Button>
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <EsimTypeDisplay type={row.esimType} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <EsimStatusDisplay status={row.esimStatus} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <CodeText value={row.iccid} maxWidth={180} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <CodeText value={row.msisdn} maxWidth={140} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <CodeText value={getSerialOrRedeem(row)} maxWidth={150} />
                </TableCell>

                <TableCell sx={cellSx.nowrap}>
                  <CodeText value={row.lpaUrl} truncate={false} color="text.secondary" />
                </TableCell>

                <TableCell align="center" sx={cellSx.nowrap}>
                  {row.expireAt ? timestampToDate(row.expireAt, 'YYYY-MM-DD') : '-'}
                </TableCell>
              </>
            );
          }}
        />
      </Box>

      <Box mt={2} display="flex" justifyContent="center">
        <StandardPagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={safeTotalCount}
          onChangePage={handleChangePage}
          size="small"
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default EsimInventories;
